import { AsyncAutocompleteField } from "@/features/FilterTable/AyncAutocompleteField";
import { DateField } from "@/features/FilterTable/DateField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { Clients } from "@/lib/clients";
import { ClientTables } from "@/lib/clientTables";
import { Tables } from "@/gen/tableNames/tables";
import { getAuthorized, postClientAction } from "@/lib/fetch";
import { getAutocompleteText } from "./generatePdfUtils";
import { S3PdfPageSelection } from "@/pages/s3/S3PdfPageSelection/columns";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";

const GeneratePdfSchema = z.object({
  client: z.preprocess(preprocessTextAsyncAutocompleteField, z.string()),
  reportingDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
  include_views: z.array(z.string()).optional(),
});

export type GeneratePdfSchemaType = z.infer<typeof GeneratePdfSchema>;
const generatePdfFields = GeneratePdfSchema.keyof().enum;

export default function S3GeneratePdf() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);

  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    register,
    formState: { isValid },
    reset,
  } = useForm<GeneratePdfSchemaType>({
    mode: "onChange",
    defaultValues: {
      [generatePdfFields.client]: "",
      [generatePdfFields.reportingDate]: undefined,
      [generatePdfFields.include_views]: [],
    },
    resolver: zodResolver(GeneratePdfSchema) as Resolver<GeneratePdfSchemaType>,
  });

  const watchedClient = watch(generatePdfFields.client);
  const clientText = getAutocompleteText(watchedClient);

  const {
    data: pdfPagesResponse,
    isLoading: isPagesLoading,
    isError: isPagesError,
  } = useQuery({
    queryKey: ["pdf-pages", clientText],
    queryFn: async () => {
      const response = await getAuthorized(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/${Clients.S3}/${Tables.S3_PDF_PAGE_SELECTION}?client_equal=${encodeURIComponent(
          clientText!,
        )}&page_size=1000`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch PDF pages");
      }
      return response.json() as Promise<{
        count: number;
        data: S3PdfPageSelection[];
      }>;
    },
    enabled: Boolean(clientText),
    refetchOnMount: false,
  });

  useEffect(() => {
    setSelectedPages([]);
    setValue(generatePdfFields.include_views, []);
  }, [clientText, setValue]);

  useEffect(() => {
    if (pdfPagesResponse?.data) {
      const defaultSelected = pdfPagesResponse.data
        .filter((page) => page.pdf_report_display === true && page.page_name)
        .map((page) => page.page_name!);
      setSelectedPages(defaultSelected);
      setValue(generatePdfFields.include_views, defaultSelected);
    }
  }, [pdfPagesResponse, setValue]);

  useEffect(() => {
    setValue(generatePdfFields.include_views, selectedPages);
  }, [selectedPages, setValue]);

  const currentPage = useCurrentPageFromUrl();
  const generatePdfMutation = useMutation({
    mutationFn: async (data: GeneratePdfSchemaType) => {
      const formattedDate = formatDateValueToISOString(data.reportingDate);

      return await postClientAction(
        `${import.meta.env.VITE_BACKEND_URL}/${Clients.S3}/${ClientTables.S3_DOWNLOAD_CENTER}/generate`,
        {
          [generatePdfFields.client]: data.client,
          prefix: Clients.S3,
          [generatePdfFields.reportingDate]: formattedDate ?? "",
          [generatePdfFields.include_views]: data.include_views,
          action_button_name: t("reports.generate_action"),
          action_execution_page: currentPage ?? "",
        },
      );
    },
    onSuccess: () => {
      toast.success(t("reports.generate_started"));
      setIsOpen(false);
      reset();
      setSelectedPages([]);
    },
    onError: () => {
      toast.error(t("reports.generate_error"));
    },
  });

  const handleFormSubmit = (data: GeneratePdfSchemaType) => {
    generatePdfMutation.mutate(data);
  };

  const handlePageSelectionChange = (pages: string[]) => {
    setSelectedPages(pages);
  };

  const pdfPages = pdfPagesResponse?.data || [];

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage: `linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {t("reports.generate_action")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-4 p-1 w-full"
        >
          <div>
            <AsyncAutocompleteField<GeneratePdfSchemaType>
              name={generatePdfFields.client}
              label="Client"
              watch={watch}
              getValues={getValues}
              setValue={setValue}
              base_url={`${Clients.S3}/${Tables.S3_PORTFOLIOS}`}
              column_name="client"
              id_column="client_id"
              showChip={false}
              keepDuplicates={false}
              required={true}
            />
          </div>

          <div>
            <DateField
              name={generatePdfFields.reportingDate}
              label={t("reports.reporting_date", { defaultValue: "Reporting date" })}
              watch={watch}
              setValue={setValue}
              register={register}
              required={true}
            />
          </div>

          {clientText && (
            <div className="space-y-2">
              <label className="text-sm text-primary">{t("reports.pages_to_include", { defaultValue: "Pages to include" })}</label>
              {isPagesError ? (
                <div className="text-sm text-danger">
                  {t("reports.pages_error", { defaultValue: "Unable to load pages." })}
                </div>
              ) : isPagesLoading ? (
                <div className="text-sm text-gray-500">
                  {t("reports.pages_loading", { defaultValue: "Loading pages..." })}
                </div>
              ) : pdfPages.length > 0 ? (
                <CheckboxGroup
                  value={selectedPages}
                  onValueChange={handlePageSelectionChange}
                  className="max-h-40 overflow-y-auto border rounded-lg p-2 overflow-x-hidden"
                >
                  {pdfPages.map((page) => (
                    <Checkbox
                      key={page.page_name}
                      value={page.page_name!}
                      className="text-sm"
                      size="sm"
                    >
                      {page.page_name}
                    </Checkbox>
                  ))}
                </CheckboxGroup>
              ) : (
                <div className="text-sm text-gray-500">
                  {t("reports.no_pages_available", { defaultValue: "No pages available for this client" })}
                </div>
              )}
            </div>
          )}

          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isDisabled={
                !isValid ||
                !clientText ||
                isPagesLoading ||
                isPagesError ||
                selectedPages.length === 0 ||
                generatePdfMutation.isPending
              }
              isLoading={generatePdfMutation.isPending}
            >
              {t("actions.generate", { defaultValue: "Generate" })}
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
}
